import React, { useEffect } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Calendar, Phone, Mail, FileText } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const ThankYou = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Track page view
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'conversion', {
        event_category: 'Contact',
        event_label: 'Thank You Page View',
      });
    }
  }, []);

  const nextSteps = [
    {
      icon: <Mail size={32} />,
      title: 'Check Your Email',
      description: 'You\'ll receive a confirmation email with your consultation details within a few minutes.',
    },
    {
      icon: <Calendar size={32} />,
      title: 'We\'ll Contact You',
      description: 'Our team will reach out at your preferred time to discuss your medical billing needs.',
    },
    {
      icon: <FileText size={32} />,
      title: 'Prepare Questions',
      description: 'Feel free to prepare any questions about our services, pricing, or implementation process.',
    },
    {
      icon: <Phone size={32} />,
      title: 'Need Immediate Help?',
      description: 'Call us at (555) 123-4567 or email info@optibillanalytics.com for urgent inquiries.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Thank You | OptiBill Analytics</title>
        <meta name="description" content="Thank you for contacting OptiBill Analytics. We'll be in touch soon!" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        paddingTop: '100px',
        paddingBottom: '80px',
      }}>
        <Container>
          {/* Success Message Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div style={{
              textAlign: 'center',
              marginBottom: '60px',
            }}>
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                style={{
                  display: 'inline-flex',
                  marginBottom: '30px',
                }}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '50%',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                }}>
                  <CheckCircle size={64} color="#4CAF50" strokeWidth={2.5} />
                </div>
              </motion.div>

              {/* Heading */}
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                style={{
                  fontSize: '48px',
                  fontWeight: 'bold',
                  color: 'white',
                  marginBottom: '20px',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}
              >
                Thank You!
              </motion.h1>

              {/* Subheading */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  fontSize: '20px',
                  color: 'rgba(255,255,255,0.95)',
                  maxWidth: '700px',
                  margin: '0 auto 40px',
                  lineHeight: '1.6',
                }}
              >
                Your consultation request has been received successfully. We're excited to help 
                optimize your medical billing and revenue cycle management!
              </motion.p>

              {/* Home Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  onClick={() => navigate('/')}
                  style={{
                    background: 'white',
                    color: '#667eea',
                    border: 'none',
                    padding: '15px 40px',
                    fontSize: '18px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 5px 20px rgba(0,0,0,0.2)';
                  }}
                >
                  <Home size={20} />
                  Return to Home
                </Button>
              </motion.div>
            </div>
          </motion.div>

          {/* Next Steps Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 style={{
              textAlign: 'center',
              fontSize: '36px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '40px',
            }}>
              What Happens Next?
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px',
              maxWidth: '1200px',
              margin: '0 auto',
            }}>
              {nextSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 + index * 0.1 }}
                >
                  <Card style={{
                    background: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '30px',
                    height: '100%',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
                  }}
                  >
                    {/* Icon */}
                    <div style={{
                      color: '#667eea',
                      marginBottom: '20px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}>
                      {step.icon}
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#1a202c',
                      marginBottom: '15px',
                      textAlign: 'center',
                    }}>
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p style={{
                      fontSize: '15px',
                      color: '#4a5568',
                      lineHeight: '1.6',
                      textAlign: 'center',
                      margin: 0,
                    }}>
                      {step.description}
                    </p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Additional Resources Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            style={{
              marginTop: '60px',
              textAlign: 'center',
            }}
          >
            <Card style={{
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '800px',
              margin: '0 auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#1a202c',
                marginBottom: '20px',
              }}>
                While You Wait...
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#4a5568',
                marginBottom: '30px',
                lineHeight: '1.6',
              }}>
                Learn more about our services and how we help healthcare providers maximize their revenue.
              </p>
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/about')}
                  style={{
                    borderWidth: '2px',
                    borderRadius: '10px',
                    padding: '10px 25px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                  }}
                >
                  About Us
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/services')}
                  style={{
                    borderWidth: '2px',
                    borderRadius: '10px',
                    padding: '10px 25px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                  }}
                >
                  Our Services
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/analytics')}
                  style={{
                    borderWidth: '2px',
                    borderRadius: '10px',
                    padding: '10px 25px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                  }}
                >
                  Analytics Dashboard
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/faq')}
                  style={{
                    borderWidth: '2px',
                    borderRadius: '10px',
                    padding: '10px 25px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                  }}
                >
                  FAQ
                </Button>
              </div>
            </Card>
          </motion.div>
        </Container>
      </div>
    </>
  );
};

export default ThankYou;
